import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { clientService } from '../../../services/clientService';
import { calculateAge, formatCPF, isValidCPFFormat } from '../../../utils/dateHelpers';

const NewClientModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    document: '',
    address: '',
    notes: '',
    birthDate: '',
    cpf: '',
    passportNumber: '',
    passportValidity: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (submitError) setSubmitError('');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Email inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setSubmitError('');

    try {
      // Save client to Supabase
      const newClient = await clientService?.create({
        name: formData?.name?.trim(),
        email: formData?.email?.trim() || null,
        phone: formData?.phone?.trim() || null,
        document: formData?.document?.trim() || null,
        address: formData?.address?.trim() || null,
        notes: formData?.notes?.trim() || null
      });

      // Call parent callback with the newly created client
      if (onSave) {
        onSave(newClient);
      }

      // Show success message
      console.log('Cliente cadastrado com sucesso:', newClient);

      // Close modal and reset form
      handleClose();
    } catch (err) {
      console.error('Erro ao cadastrar cliente:', err);
      setSubmitError(err?.message || 'Erro ao cadastrar cliente. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      document: '',
      address: '',
      notes: '',
      birthDate: '',
      cpf: '',
      passportNumber: '',
      passportValidity: ''
    });
    setErrors({});
    setSubmitError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div className="relative bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Icon name="UserPlus" size={24} />
            Cadastrar Novo Cliente
          </h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-2 hover:bg-muted rounded-md transition-colors disabled:opacity-50"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {submitError && (
            <div className="mb-4 p-4 bg-error/10 border border-error rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-error font-medium mb-1">Erro ao cadastrar cliente</p>
                <p className="text-xs text-error/80">{submitError}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* Informações Básicas */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="User" size={16} />
                Informações Básicas
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Nome Completo *
                  </label>
                  <Input
                    type="text"
                    value={formData?.name}
                    onChange={(e) => handleChange('name', e?.target?.value)}
                    placeholder="Digite o nome completo"
                    disabled={loading}
                    className={errors?.name ? 'border-error' : ''}
                  />
                  {errors?.name && (
                    <p className="text-error text-xs mt-1 flex items-center gap-1">
                      <Icon name="AlertCircle" size={12} />
                      {errors?.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={formData?.email}
                    onChange={(e) => handleChange('email', e?.target?.value)}
                    placeholder="email@exemplo.com"
                    disabled={loading}
                    className={errors?.email ? 'border-error' : ''}
                  />
                  {errors?.email && (
                    <p className="text-error text-xs mt-1 flex items-center gap-1">
                      <Icon name="AlertCircle" size={12} />
                      {errors?.email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Telefone
                  </label>
                  <Input
                    type="tel"
                    value={formData?.phone}
                    onChange={(e) => handleChange('phone', e?.target?.value)}
                    placeholder="(00) 00000-0000"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                    Data de Nascimento
                    {formData?.birthDate && (
                      <span className="text-xs text-primary font-normal">
                        ({calculateAge(formData?.birthDate)} anos)
                      </span>
                    )}
                  </label>
                  <Input
                    type="date"
                    value={formData?.birthDate}
                    onChange={(e) => handleChange('birthDate', e?.target?.value)}
                    disabled={loading}
                    max={new Date()?.toISOString()?.split('T')?.[0]}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    CPF
                  </label>
                  <Input
                    type="text"
                    value={formData?.cpf}
                    onChange={(e) => {
                      const formatted = formatCPF(e?.target?.value);
                      handleChange('cpf', formatted);
                    }}
                    placeholder="000.000.000-00"
                    disabled={loading}
                    maxLength={14}
                  />
                  {formData?.cpf && !isValidCPFFormat(formData?.cpf) && (
                    <p className="text-warning text-xs mt-1 flex items-center gap-1">
                      <Icon name="AlertCircle" size={12} />
                      Formato de CPF inválido
                    </p>
                  )}
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Documento (RG/CNPJ/Outros)
                  </label>
                  <Input
                    type="text"
                    value={formData?.document}
                    onChange={(e) => handleChange('document', e?.target?.value)}
                    placeholder="Outros documentos de identificação"
                    disabled={loading}
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Endereço
                  </label>
                  <Input
                    type="text"
                    value={formData?.address}
                    onChange={(e) => handleChange('address', e?.target?.value)}
                    placeholder="Rua, número, bairro, cidade, estado"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Dados de Passaporte */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Globe" size={16} />
                Dados de Passaporte
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Número do Passaporte
                  </label>
                  <Input
                    type="text"
                    value={formData?.passportNumber}
                    onChange={(e) => handleChange('passportNumber', e?.target?.value?.toUpperCase())}
                    placeholder="XX000000"
                    disabled={loading}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Validade do Passaporte
                  </label>
                  <Input
                    type="date"
                    value={formData?.passportValidity}
                    onChange={(e) => handleChange('passportValidity', e?.target?.value)}
                    disabled={loading}
                    min={new Date()?.toISOString()?.split('T')?.[0]}
                  />
                </div>
              </div>
            </div>

            {/* Observações */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="MessageSquare" size={16} />
                Observações
              </h3>
              <textarea
                value={formData?.notes}
                onChange={(e) => handleChange('notes', e?.target?.value)}
                placeholder="Informações adicionais sobre o cliente..."
                rows={4}
                disabled={loading}
                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              />
            </div>
          </div>
        </form>

        <div className="sticky bottom-0 bg-card border-t border-border p-4 flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="default"
            iconName={loading ? undefined : "Check"}
            iconPosition="left"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Cadastrando...</span>
              </div>
            ) : (
              'Cadastrar Cliente'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewClientModal;